import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';
import { Card, Page, PageHeader, StatCard, Tabs } from '../shared/ui';
import { PosrIntermediaries } from './posrednici/posrIntermediaries';
import { PosrLeads } from './posrednici/posrLeads';
import { PosrAttributions, PosrSettlements } from './posrednici/posrSettlements';
import { PosrChannel, PosrPriceLists, PosrScores } from './posrednici/posrReports';

/**
 * m.1.2 Posrednici (mail UID 966/967, Vedran 26.10.2026): registar posrednika, prijava leada,
 * pripis kupnje, mjesecni obracun provizije, tromjesecna ocjena i kanal Posrednik.
 * Backend: App\Http\Controllers\Api\PosredniciController, /api/posrednici/*.
 */

type TabKey = 'registar' | 'prijave' | 'pripis' | 'obracun' | 'cjenik' | 'ocjena' | 'kanal';

type Overview = {
    is_admin: boolean;
    intermediaries_by_status: Record<string, number>;
    leads_by_outcome: Record<string, number>;
    leads_overdue: number;
};

export function PosredniciModule({ token }: { token: string | null; locale?: 'hr' | 'en' }) {
    const [tab, setTab] = useState<TabKey>('registar');
    const [ov, setOv] = useState<Overview | null>(null);
    const [msg, setMsg] = useState<string | null>(null);

    const loadOverview = useCallback(() => {
        api<Overview>('/posrednici/overview', token).then(setOv).catch((e: Error) => setMsg(e.message));
    }, [token]);
    useEffect(loadOverview, [loadOverview, tab]);

    const onError = useCallback((m: string) => setMsg(m), []);
    const props = { token, isAdmin: Boolean(ov?.is_admin), onError };
    const active = (ov?.intermediaries_by_status.aktivan ?? 0) + (ov?.intermediaries_by_status.probni ?? 0);

    return (
        <Page>
            <PageHeader icon="🤝" title="1.2 Posrednici" subtitle="Registar posrednika, prijave klijenata, provizija i ocjena kanala" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
                <StatCard icon="👥" color="#5b6172" value={active} label="Aktivni i u probnom roku" />
                <StatCard icon="🕓" color="#9a6700" value={ov?.intermediaries_by_status.kandidat ?? 0} label="Kandidati u provjeri" />
                <StatCard icon="📨" color="#5b6172" value={ov?.leads_by_outcome.ceka ?? 0} label="Prijave čekaju odluku" />
                <StatCard icon="⏰" color="#c0264a" value={ov?.leads_overdue ?? 0} label="Odluka kasni" />
            </div>
            {msg ? (
                <Card pad={12} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem' }}>{msg}</span>
                    <button type="button" onClick={() => setMsg(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700 }}>×</button>
                </Card>
            ) : null}
            <Tabs<TabKey> active={tab} onChange={setTab} tabs={[
                { key: 'registar', label: 'Posrednici' },
                { key: 'prijave', label: 'Prijave' },
                { key: 'pripis', label: 'Pripis kupnji' },
                { key: 'obracun', label: 'Obračun' },
                { key: 'cjenik', label: 'Cjenik' },
                { key: 'ocjena', label: 'Ocjena' },
                { key: 'kanal', label: 'Kanal' },
            ]} />
            {tab === 'registar' ? <PosrIntermediaries {...props} /> : null}
            {tab === 'prijave' ? <PosrLeads {...props} /> : null}
            {tab === 'pripis' ? <PosrAttributions {...props} /> : null}
            {tab === 'obracun' ? <PosrSettlements {...props} /> : null}
            {tab === 'cjenik' ? <PosrPriceLists {...props} /> : null}
            {tab === 'ocjena' ? <PosrScores {...props} /> : null}
            {tab === 'kanal' ? <PosrChannel {...props} /> : null}
        </Page>
    );
}
